import { useLayoutEffect } from "react";

/** Visible height of the app shell on compact screens. */
export const APP_HEIGHT_VAR = "--app-height";

/**
 * Marks the document while compact layout locks the viewport.
 * Overflow is this class in globals.css, not an inline style, so a MUI modal
 * can keep its own `body.style.overflow` across enable and disable.
 */
export const COMPACT_VIEWPORT_LOCK_CLASS = "compact-viewport-lock";

/** Frames after mount while the mobile browser toolbar is still settling. */
const SETTLE_FRAMES = 3;

const DOCUMENT_LOCK_PROPS = ["height", "min-height", "max-height"] as const;

/**
 * Keeps a reported visual height from growing past the dynamic viewport.
 * A phone can report the large viewport once and never fire resize, which
 * pushes the pagination footer under the fixed bottom nav.
 */
export function capVisibleHeight(visualHeight: number, dynamicHeight: number): number {
  if (dynamicHeight <= 0) {
    return visualHeight;
  }
  return Math.min(visualHeight, dynamicHeight);
}

/** `100dvh` in pixels. Zero when the browser cannot measure it. */
export function readDynamicViewportHeight(): number {
  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.top = "0";
  probe.style.height = "100dvh";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  document.documentElement.append(probe);
  const height = probe.getBoundingClientRect().height;
  probe.remove();
  return height;
}

/** Visible viewport height, never taller than `100dvh`. Falls back to innerHeight. */
export function readVisualViewportHeight(): number {
  const visual = window.visualViewport?.height ?? window.innerHeight;
  return capVisibleHeight(visual, readDynamicViewportHeight());
}

/**
 * Shell height. On a phone `100vh` is the large viewport and wins over
 * `max-height: 100dvh`, so the pagination footer lands under the fixed bottom
 * nav. Compact screens lock to the visible height instead. `100svh` is the
 * pre-JS fallback: it is never taller than the screen with the browser toolbar
 * open. Desktop pages grow with the document (`min-height: 100vh`).
 *
 * `fillViewport` pins a desktop shell to the screen so an inner region can
 * scroll while the app header stays put. AI Assist uses it: only the
 * transcript scrolls, and the composer stays at the bottom of the page.
 */
export function shellHeightSx(
  compact: boolean,
  fillViewport = false,
): {
  height?: string;
  minHeight: number | string;
  maxHeight?: string;
} {
  if (!compact) {
    if (fillViewport) {
      return { height: "100vh", minHeight: 0, maxHeight: "100vh" };
    }
    return { minHeight: "100vh" };
  }
  const height = `var(${APP_HEIGHT_VAR}, 100svh)`;
  return {
    height,
    minHeight: 0,
    maxHeight: height,
  };
}

function documentLockTargets(): HTMLElement[] {
  const targets = [document.documentElement, document.body];
  const app = document.getElementById("root");
  if (app) {
    targets.push(app);
  }
  return targets;
}

/** Stops `min-height: 100%` from leaving a scrollable large viewport under the shell. */
function lockDocument(height: string) {
  document.documentElement.classList.add(COMPACT_VIEWPORT_LOCK_CLASS);
  for (const el of documentLockTargets()) {
    el.style.setProperty("height", height);
    el.style.setProperty("min-height", "0px");
    el.style.setProperty("max-height", height);
  }
}

function unlockDocument() {
  document.documentElement.classList.remove(COMPACT_VIEWPORT_LOCK_CLASS);
  for (const el of documentLockTargets()) {
    for (const prop of DOCUMENT_LOCK_PROPS) {
      el.style.removeProperty(prop);
    }
  }
}

/** Publishes the visible viewport height while compact layout is on. */
export function useVisualViewportHeight(enabled: boolean): void {
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    const root = document.documentElement;
    let frames = 0;
    let rafId = 0;
    const publish = () => {
      const height = `${readVisualViewportHeight()}px`;
      root.style.setProperty(APP_HEIGHT_VAR, height);
      lockDocument(height);
    };
    const settle = () => {
      publish();
      frames += 1;
      if (frames < SETTLE_FRAMES) {
        rafId = requestAnimationFrame(settle);
      }
    };
    publish();
    rafId = requestAnimationFrame(settle);
    window.addEventListener("resize", publish);
    window.addEventListener("orientationchange", publish);
    window.addEventListener("pageshow", publish);
    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", publish);
    viewport?.addEventListener("scroll", publish);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", publish);
      window.removeEventListener("orientationchange", publish);
      window.removeEventListener("pageshow", publish);
      viewport?.removeEventListener("resize", publish);
      viewport?.removeEventListener("scroll", publish);
      root.style.removeProperty(APP_HEIGHT_VAR);
      unlockDocument();
    };
  }, [enabled]);
}
